import {
  Alert,
  Card,
  CardBody,
  Form,
  Heading,
  Paragraph,
  ProductSelect,
  Selectable,
  SelectableItem,
  Separator,
  useMatchMediaTierProp,
} from "@george-labs.com/design-system"
import { ProductItem } from "@george-labs.com/design-system/types/components/Select/ProductSelect"
import { SetAccountNoRq } from "@smartx/mikro-vt-client-api"
import { useFormik } from "formik"
import { useContext } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import * as Yup from "yup"
import { offerSetAccountNoApi } from "../../api/apiClient"
import { useOfferData } from "../../app/hooks"
import { useOfferScreenFlow } from "../../hooks/useOfferScreenFlow"
import AuthContext from "../../providers/AuthProvider"
import { RequestHeader } from "../../utils/utils"
import OfferAccountInfo, { getAccountItemProps } from "./OfferAccountInfo"
import { OfferNavButtons, OfferSaveButton } from "./OfferNavButtons"
import { useSaveModal } from "./OfferModalContext"

const OfferAccountNumbers = () => {
  const authContext = useContext(AuthContext)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const offerData = useOfferData()
  const { goToNextOfferScreen } = useOfferScreenFlow()
  const { openErrorModal } = useSaveModal()

  const formik = useFormik({
    initialValues: {
      selectedAccount: offerData?.accounts
        ?.filter((el) => el.selected)
        ?.map((el) => el.accountNo),
    },
    validationSchema: Yup.object({
      selectedAccount: Yup.array().min(1).max(1),
    }),
    onSubmit: (values) => handleSubmit(values),
  })

  const saveButtonHandler = () => {
    save(false)
  }

  const handleSubmit = (_values: any) => {
    save(true)
  }

  const save = async (nextStep: boolean) => {
    const setNextStep = offerData.offerState === "ACCOUNTS" ? nextStep : false

    authContext.refresh()

    const rq: SetAccountNoRq = {
      header: new RequestHeader(),
      caseId: offerData?.caseId,
      accountNo: formik.values.selectedAccount?.[0] || null,
      nextStep: setNextStep,
    }

    try {
      const result = await offerSetAccountNoApi.setAccountNo(rq)
      if (!result?.data?.success) {
        throw new Error("Account numbers error")
      }

      if (nextStep) {
        goToNextOfferScreen(offerData?.caseId)
      } else {
        navigate("/dashboard", { replace: true })
      }
    } catch {
      openErrorModal(() => goToNextOfferScreen(offerData?.caseId))
    }
  }

  const headerContent = (
    <Heading level={1}>{t("offers.accountNumbers.title")}</Heading>
  )

  const textContent = (
    <Paragraph className="mb-3">{t("offers.accountNumbers.text")}</Paragraph>
  )

  const accountNumberListContent =
    !offerData?.accounts?.length || offerData?.accounts?.length <= 5 ? (
      <Card nested>
        <Selectable
          type={Selectable.TYPE.RADIO}
          verticalAlign={Selectable.VERTICAL_ALIGN.MIDDLE}
          selectOnClick={true}
          selectedItems={formik.values.selectedAccount}
          onSelect={(selected) =>
            formik.setFieldValue("selectedAccount", selected)
          }
        >
          {offerData?.accounts?.map((accInfo, idx) => (
            <>
              <Card key={`${accInfo.accountNo}-${idx}`}>
                <SelectableItem
                  value={accInfo.accountNo}
                  label={accInfo.accountNo}
                >
                  <OfferAccountInfo accInfo={accInfo} />
                </SelectableItem>
              </Card>
              {idx < offerData.accounts.length - 1 && <Separator />}
            </>
          ))}
        </Selectable>
      </Card>
    ) : (
      <ProductSelect
        name="productSelect"
        placeholder={t("offers.accountNumbers.title")}
        onSelect={(selected: ProductItem) =>
          formik.setFieldValue("selectedAccount", [selected.value])
        }
        items={offerData?.accounts?.map((el) => getAccountItemProps(el))}
      />
    )

  const navButtonsContent = <OfferNavButtons />

  const saveButtonContent = (
    <OfferSaveButton onSaveButtonClicked={saveButtonHandler} />
  )

  const warning = (
    <>
      {!formik.isValid && !!formik.submitCount && (
        <Alert heading="" variant={Alert.VARIANT.WARNING}>
          {t("offers.accountNumbers.warning")}
        </Alert>
      )}
    </>
  )

  const pageContent = useMatchMediaTierProp({
    xs: (
      <>
        {headerContent}

        {textContent}

        {accountNumberListContent}

        {warning}

        {navButtonsContent}
      </>
    ),
    md: (
      <Card nested={false}>
        <CardBody>
          {headerContent}

          {textContent}

          {accountNumberListContent}

          {warning}
        </CardBody>

        <Separator />

        <CardBody>{navButtonsContent}</CardBody>
      </Card>
    ),
  })

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault()
        formik.handleSubmit()
      }}
    >
      {pageContent}

      {saveButtonContent}
    </Form>
  )
}

export default OfferAccountNumbers
